import type { SharedSiteView } from "@panelgenie/domain";
import { Banner, Card, Heading } from "@panelgenie/shared-ui";

/**
 * Read-only landing page for a `SharedSiteView` — the site-scope
 * variant of the share landing (`SharedPanelLanding` is the
 * panel-scope counterpart).
 *
 * Renders in document order:
 *   1. Site identity block (name + address)
 *   2. Per-panel summary cards (manufacturer / model / amperage /
 *      installation count + safety advisory when applicable)
 *   3. Safety disclaimer footer
 *
 * Safety advisory rendering is unconditional: the R-SAFE-3 contract
 * ("safety information appears in every tier including Lite/free")
 * is enforced upstream by `buildSharedView`; this component renders
 * whatever advisory the projection emits.
 *
 * Read-only by construction — no callbacks, no inputs, no edit
 * affordances.
 */
export type SharedSiteLandingProps = {
  readonly view: SharedSiteView;
};

export const SharedSiteLanding = ({ view }: SharedSiteLandingProps) => {
  const { site, panels, disclaimer } = view;
  return (
    <article
      aria-label="shared-site-landing"
      className="mx-auto max-w-3xl px-4 py-6 text-fg"
    >
      <header>
        <p className="m-0 text-xs uppercase tracking-wider text-mute">
          Shared by Panel Genie — view only
        </p>
        <Heading level="h1" className="mt-1">
          {site.name}
        </Heading>
        <p className="mt-1 text-base text-mute">
          {site.address.street}, {site.address.city}, {site.address.region}{" "}
          {site.address.postalCode}, {site.address.country}
        </p>
      </header>

      <section aria-label="shared-site-panels" className="mt-6">
        <Heading level="h2" className="mb-3">
          Panels
        </Heading>
        {panels.length === 0 ? (
          <p className="text-base text-mute">This site has no panels yet.</p>
        ) : (
          <ul className="list-none p-0 m-0 flex flex-col gap-3">
            {panels.map((p) => (
              <li key={p.panelId}>
                <Card aria-label={`shared-panel-summary-${p.panelId}`}>
                  <Card.Body>
                    <header className="flex items-baseline justify-between gap-3">
                      <Heading level="h3">
                        {p.manufacturer ?? "Unknown manufacturer"}
                        {p.model !== undefined ? ` ${p.model}` : ""}
                      </Heading>
                      <span className="text-sm text-mute flex-none">
                        {p.mainAmperage !== undefined
                          ? `${p.mainAmperage} A main`
                          : "Main amperage not recorded"}
                      </span>
                    </header>
                    <p className="mt-2 text-sm text-mute">
                      {p.columns} × {p.rows} positions · {p.installationCount}{" "}
                      installation{p.installationCount === 1 ? "" : "s"}
                    </p>
                    {p.safetyAdvisory !== undefined ? (
                      <SafetyAdvisoryBlock advisory={p.safetyAdvisory} />
                    ) : null}
                  </Card.Body>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Banner
        variant="info"
        aria-label="shared-site-disclaimer"
        title="Safety disclaimer"
        className="mt-6"
      >
        {disclaimer.body}
      </Banner>
    </article>
  );
};

type SafetyAdvisoryBlockProps = {
  readonly advisory: NonNullable<
    SharedSiteView["panels"][number]["safetyAdvisory"]
  >;
};

const SafetyAdvisoryBlock = ({ advisory }: SafetyAdvisoryBlockProps) => {
  return (
    <Banner
      variant="warning"
      aria-label="shared-panel-safety-advisory"
      title={`Safety advisory — ${advisory.entry.displayName}`}
      className="mt-3"
    >
      <p className="m-0">{advisory.entry.concern}</p>
      <p className="mt-1 m-0 text-xs text-mute">
        Source: {advisory.entry.source.label}
      </p>
    </Banner>
  );
};
